import React from 'react';
import { Select, Spin, Typography, Space, Row, Col, Badge, Avatar } from 'antd';
import { FacebookFilled, WhatsAppOutlined, MessageOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';

const { Option } = Select;
const { Text } = Typography;

const AgentSelect = ({ agents, loading, value, onChange, onSearch }) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        border: isDarkMode ? '#434343' : '#f0f0f0',
        hoverBg: isDarkMode ? '#2a2a2a' : '#fafafa',
        shadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 2px 8px rgba(0, 0, 0, 0.15)',
        selectedBg: isDarkMode ? '#1f1f1f' : '#e6f7ff'
    };

    const getPlatformIcon = (platformType) => {
        switch (platformType?.toLowerCase()) {
            case 'messenger':
                return <FacebookFilled style={{ color: '#1877F2', fontSize: '16px' }} />;
            case 'whatsapp':
                return <WhatsAppOutlined style={{ color: '#25D366', fontSize: '16px' }} />;
            default:
                return <MessageOutlined style={{ color: isDarkMode ? '#8c8c8c' : '#595959', fontSize: '16px' }} />;
        }
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            started: isDarkMode ? '#49aa19' : '#52c41a',
            stopped: isDarkMode ? '#d32029' : '#f5222d',
            default: isDarkMode ? '#434343' : '#d9d9d9'
        };

        switch (status?.toLowerCase()) {
            case 'started':
                return <Badge dot color={statusColors.started} />;
            case 'stopped':
                return <Badge dot color={statusColors.stopped} />;
            default:
                return <Badge dot color={statusColors.default} />;
        }
    };

    const selectDropdownRender = (menu) => (
        <div style={{
            background: themeColors.background,
            borderRadius: '8px',
            boxShadow: themeColors.shadow,
            border: `1px solid ${themeColors.border}`
        }}>
            {menu}
        </div>
    );

    const ellipsisStyle = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        color: themeColors.text,
        backgroundColor: 'transparent'
    };

    // Custom styles for the CSS overrides
    const customStyles = `
        .ant-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
            background-color: ${themeColors.selectedBg} !important;
            color: ${themeColors.text} !important;
        }
        
        .ant-select-dropdown .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
            background-color: ${themeColors.hoverBg} !important;
        }

        .ant-select-dropdown .ant-typography {
            background-color: transparent !important;
            color: ${themeColors.text} !important;
        }

        .ant-select-dropdown .ant-select-item-option-content {
            background-color: transparent !important;
        }

        .ant-select-dropdown .ant-select-item-option-content > div {
            background-color: transparent !important;
        }
        
        .ant-select-dropdown .ant-select-item-option-content .ant-row,
        .ant-select-dropdown .ant-select-item-option-content .ant-col,
        .ant-select-dropdown .ant-select-item-option-content .ant-space {
            background-color: transparent !important;
        }
    `;

    return (
        <>
            <style>{customStyles}</style>
            <Select
                showSearch
                loading={loading}
                value={value}
                onChange={onChange}
                onSearch={onSearch}
                style={{ width: '100%' }}
                placeholder={t('select_an_agent')}
                optionLabelProp="label"
                notFoundContent={loading ? <Spin size="small" style={{ color: themeColors.text }} /> : null}
                filterOption={false}
                dropdownRender={selectDropdownRender}
                dropdownStyle={{
                    padding: '8px 0',
                    backgroundColor: themeColors.background
                }}
                listItemHeight={72}
                listHeight={320}
                suffixIcon={loading ? <Spin size="small" style={{ color: themeColors.text }} /> : undefined}
                className={isDarkMode ? 'dark-mode-select' : 'light-mode-select'}
            >
                {agents.map(agent => (
                    <Option
                        key={agent.id}
                        value={agent.id}
                        className="agent-option"
                        label={
                            <Row align="middle" style={{ width: '100%', backgroundColor: 'transparent' }}>
                                <Space size={12} style={{ backgroundColor: 'transparent' }}>
                                    <Avatar
                                        size="small"
                                        src={agent.platform_account?.picture_url}
                                        icon={!agent.platform_account?.picture_url && <UserOutlined />}
                                        style={{
                                            backgroundColor: agent.status === 'started'
                                                ? (isDarkMode ? '#49aa19' : '#52c41a')
                                                : (isDarkMode ? '#d32029' : '#f5222d'),
                                        }}
                                    />
                                    <Text strong style={ellipsisStyle}>{agent.name}</Text>
                                </Space>
                            </Row>
                        }
                    >
                        <Row align="middle" style={{
                            padding: '4px 0',
                            width: '100%',
                            backgroundColor: 'transparent',
                        }} wrap={false}>
                            <Col flex="auto" style={{ minWidth: 0, marginInlineEnd: '16px', overflow: 'hidden', backgroundColor: 'transparent' }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%', backgroundColor: 'transparent' }}>
                                    <div style={{ flexShrink: 0, marginInlineEnd: '12px', backgroundColor: 'transparent' }}>
                                        <Avatar
                                            size="small"
                                            src={agent.platform_account?.picture_url}
                                            icon={!agent.platform_account?.picture_url && getPlatformIcon(agent.platform?.platform_type)}
                                            style={{
                                                backgroundColor: agent.status === 'started'
                                                    ? (isDarkMode ? '#49aa19' : '#52c41a')
                                                    : (isDarkMode ? '#d32029' : '#f5222d'),
                                            }}
                                        />
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1, backgroundColor: 'transparent' }}>
                                        <Text strong style={ellipsisStyle}>{agent.name}</Text>
                                        <Text style={{
                                            ...ellipsisStyle,
                                            fontSize: '12px',
                                            color: themeColors.secondaryText
                                        }}>
                                            {agent.platform_account?.page_name}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                            <Col flex="none" style={{ flexShrink: 0, backgroundColor: 'transparent' }}>
                                <Space align="center" size={4} style={{ backgroundColor: 'transparent' }}>
                                    {getStatusBadge(agent.status)}
                                    <Text style={{
                                        fontSize: '12px',
                                        textTransform: 'capitalize',
                                        backgroundColor: 'transparent',
                                        color: agent.status === 'started'
                                            ? (isDarkMode ? '#49aa19' : '#52c41a')
                                            : (isDarkMode ? '#d32029' : '#f5222d')
                                    }}>
                                        {agent.status}
                                    </Text>
                                </Space>
                            </Col>
                        </Row>
                    </Option>
                ))}
            </Select>
        </>
    );
};

export default AgentSelect;
